import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

// Input -> machine -> output diagram
const MachineDiagram: React.FC<{ input: string; box: string; output: string }> = ({ input, box, output }) => (
 <div className="my-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-6">
  <div className="flex items-center justify-center gap-3 sm:gap-5 flex-wrap">
   <div className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm font-medium text-gray-700 dark:text-gray-200 text-center">{input}</div>
   <span className="text-gray-400 text-xl">→</span>
   <div className="px-5 py-3 rounded-lg border-2 border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 text-sm font-bold text-amber-700 dark:text-amber-300 text-center">{box}</div>
   <span className="text-gray-400 text-xl">→</span>
   <div className="px-4 py-2.5 rounded-lg border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-sm font-medium text-green-700 dark:text-green-300 text-center">{output}</div>
  </div>
 </div>
);

export const JSFunctions: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Functions</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    Reusable blocks of logic — write once, run anywhere. Functions are the building blocks that organize code and unlock every advanced JavaScript concept.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="15 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>In an e-commerce app, every order needs the same steps: validate the user, calculate the total, apply a discount, calculate tax, generate an invoice, send a notification. Without functions, you&apos;d copy that logic for every order:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// Order 1\nvalidate user logic...\ncalculate total logic...\napply discount logic...\n\n// Order 2\nvalidate user logic...   // exact same code again\ncalculate total logic...\napply discount logic...` }]} defaultLanguage="javascript" id="fn-duplication" />
   <Para>That means duplication, harder maintenance, more bugs, and poor readability. We need to write logic <Mark>once</Mark>, reuse it many times, and organize code into meaningful units. That&apos;s what functions are for.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">A <Mark>function</Mark> is a reusable block of code designed to perform a specific task — like a machine that takes input, processes it, and produces output.</p>
   </div>
   <MachineDiagram input="a, b" box="add()" output="a + b" />
   <CodeBlock snippets={[{ language: 'javascript', code: `function add(a, b) {\n  return a + b;\n}` }]} defaultLanguage="javascript" id="fn-definition" />
  </section>

  {/* 3. Why functions exist */}
  <section className="mb-12">
   <SectionHeading n={3}>Why Functions Exist</SectionHeading>
   <Para>Without functions, every calculation is rewritten:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const order1Total = 1000 + 200;\nconst order2Total = 1500 + 300;\nconst order3Total = 2000 + 400;` }]} defaultLanguage="javascript" id="fn-without" />
   <Para>With a function, there&apos;s one implementation and unlimited reuse:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function calculateTotal(price, tax) {\n  return price + tax;\n}\n\ncalculateTotal(1000, 200);\ncalculateTotal(1500, 300);\ncalculateTotal(2000, 400);` }]} defaultLanguage="javascript" id="fn-with" />
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea</SectionHeading>
   <Para>Functions let you break a large problem into smaller, named pieces. A &quot;Place Order&quot; flow becomes a handful of focused functions instead of one massive block:</Para>
   <div className="my-6 flex flex-wrap gap-2">
    {['validateUser()', 'calculateBill()', 'processPayment()', 'assignRider()', 'sendNotification()'].map((f) => <Pill key={f}>{f}</Pill>)}
   </div>
  </section>

  {/* 5. Declaration */}
  <section className="mb-12">
   <SectionHeading n={5}>Function Declaration</SectionHeading>
   <Para>The most common way to create a function — declare it, then call it by name:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function greet() {\n  console.log("Hello");\n}\n\ngreet();` }]} defaultLanguage="javascript" id="fn-declaration" />
   <Output>Hello</Output>
  </section>

  {/* 6. Parameters & arguments */}
  <section className="mb-12">
   <SectionHeading n={6}>Parameters &amp; Arguments</SectionHeading>
   <Para>Functions get powerful when they accept inputs:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function greet(name) {\n  console.log(\`Hello \${name}\`);\n}\n\ngreet("Sachin");\ngreet("Rahul");` }]} defaultLanguage="javascript" id="fn-params" />
   <Output>{`Hello Sachin\nHello Rahul`}</Output>
   <Callout tone="info" title="Parameter vs argument">
    <Code>name</Code> in the definition is a <Mark>parameter</Mark> (a placeholder). <Code>"Sachin"</Code> at the call site is an <Mark>argument</Mark> (the actual value).
   </Callout>
  </section>

  {/* 7. Return */}
  <section className="mb-12">
   <SectionHeading n={7}>Return Values</SectionHeading>
   <Para>A function can hand a value back to whoever called it using <Code>return</Code>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function add(a, b) {\n  return a + b;\n}\n\nconst result = add(10, 20);\nconsole.log(result);` }]} defaultLanguage="javascript" id="fn-return" />
   <Output>30</Output>
   <Callout tone="warning" title="No return = undefined">A function with no <Code>return</Code> hands back <Code>undefined</Code>. <Code>{`function add(a,b){ a + b; }`}</Code> computes the sum but never returns it.</Callout>
  </section>

  {/* 8. Execution flow */}
  <section className="mb-12">
   <SectionHeading n={8}>Function Execution Flow</SectionHeading>
   <Para>When a function is called, JavaScript pauses the current code, runs the function, then resumes where it left off:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function greet() {\n  console.log("Hello");\n}\n\nconsole.log("Start");\ngreet();\nconsole.log("End");` }]} defaultLanguage="javascript" id="fn-flow" />
   <Output>{`Start\nHello\nEnd`}</Output>
   <Para>This call-and-return model is the foundation for the <Mark>call stack</Mark>, <Mark>closures</Mark>, the <Mark>event loop</Mark>, and async programming.</Para>
  </section>

  {/* 9. Function expressions */}
  <section className="mb-12">
   <SectionHeading n={9}>Function Expressions</SectionHeading>
   <Para>Because functions are values, they can be stored in variables:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const greet = function () {\n  console.log("Hello");\n};\n\ngreet();` }]} defaultLanguage="javascript" id="fn-expression" />
   <Output>Hello</Output>
  </section>

  {/* 10. First-class */}
  <section className="mb-12">
   <SectionHeading n={10}>Functions Are First-Class Citizens</SectionHeading>
   <Para>One of JavaScript&apos;s most important ideas — functions are treated like any other value. They can be:</Para>
   <div className="my-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
    {['Assigned to variables', 'Passed as arguments', 'Returned from other functions', 'Stored inside objects'].map((x) => (
     <div key={x} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-[13px] text-gray-700 dark:text-gray-300">
      <span className="text-green-500">✓</span>{x}
     </div>
    ))}
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `const sayHello = function () {\n  console.log("Hello");\n};\n\nfunction execute(fn) {\n  fn();           // run the function we were given\n}\n\nexecute(sayHello); // pass a function as an argument` }]} defaultLanguage="javascript" id="fn-firstclass" />
   <Output>Hello</Output>
   <Para>This capability powers callbacks, promises, event handlers, middleware, and functional programming.</Para>
  </section>

  {/* 11. Default params */}
  <section className="mb-12">
   <SectionHeading n={11}>Default Parameters</SectionHeading>
   <Para>Provide a fallback when an argument is missing:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function greet(name = "Guest") {\n  console.log(\`Hello \${name}\`);\n}\n\ngreet();          // Hello Guest\ngreet("Sachin");  // Hello Sachin` }]} defaultLanguage="javascript" id="fn-default" />
  </section>

  {/* 12. Rest params */}
  <section className="mb-12">
   <SectionHeading n={12}>Rest Parameters</SectionHeading>
   <Para>Collect any number of arguments into an array — useful when the count is unknown:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function sum(...numbers) {\n  let total = 0;\n  for (const num of numbers) {\n    total += num;\n  }\n  return total;\n}\n\nsum(10, 20, 30, 40); // 100` }]} defaultLanguage="javascript" id="fn-rest" />
   <Output>100</Output>
  </section>

  {/* 13. Higher order */}
  <section className="mb-12">
   <SectionHeading n={13}>Higher-Order Functions</SectionHeading>
   <Para>A higher-order function either <Mark>accepts a function</Mark> as an argument or <Mark>returns a function</Mark>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function createGreeter() {\n  return function (name) {\n    console.log(\`Hello \${name}\`);\n  };\n}\n\nconst greet = createGreeter();\ngreet("Sachin");` }]} defaultLanguage="javascript" id="fn-hof" />
   <Output>Hello Sachin</Output>
   <Para>Higher-order functions are everywhere in React, Node.js, Express, and functional programming.</Para>
  </section>

  {/* 14. Real world */}
  <section className="mb-12">
   <SectionHeading n={14}>Real-World Example</SectionHeading>
   <Para>An order-total calculator — written once, reused throughout the app:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function calculateTotal(amount, tax, discount) {\n  return amount + tax - discount;\n}\n\nconst total = calculateTotal(1000, 100, 50);\nconsole.log(total);` }]} defaultLanguage="javascript" id="fn-realworld" />
   <Output>1050</Output>
  </section>

  {/* 15. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={15}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Forgetting return">A function that computes but doesn&apos;t <Code>return</Code> gives back <Code>undefined</Code>.</Callout>
   <Callout tone="warning" title="Giant functions">A 500-line <Code>processOrder()</Code> should be split into small focused functions: <Code>validateOrder()</Code>, <Code>calculateTotal()</Code>, <Code>processPayment()</Code>…</Callout>
   <Callout tone="warning" title="Too many parameters">Instead of <Code>createUser(name, age, city, country, phone, email, …)</Code>, pass one object: <Code>createUser(userDetails)</Code>.</Callout>
   <Callout tone="warning" title="Doing multiple things">A function named <Code>saveUserAndSendEmailAndGenerateReport()</Code> violates the Single Responsibility Principle. One function, one job.</Callout>
  </section>

  {/* 16. Interview concepts */}
  <section className="mb-12">
   <SectionHeading n={16}>Interview Concepts</SectionHeading>
   <div className="flex flex-wrap gap-2">
    {['First-class functions', 'Declaration vs expression', 'Higher-order functions', 'Default parameters', 'Rest parameters', 'How execution works internally'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
  </section>

  {/* 17. Mental model */}
  <section className="mb-12">
   <SectionHeading n={17}>Mental Model</SectionHeading>
   <Para>Think of a function as a coffee machine: the same machine, reused infinitely — input goes in, processing happens, output comes out.</Para>
   <MachineDiagram input="Beans + Water" box="Coffee Machine" output="Coffee" />
  </section>

  {/* 18 & 19 */}
  <section className="mb-4">
   <SectionHeading n={18}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     A function is a reusable block of code that can accept inputs, perform operations, and optionally return a result.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Functions are the building blocks of JavaScript. They organize logic, eliminate duplication, improve maintainability, and enable advanced concepts like callbacks, closures, promises, and async programming. Master them before any advanced topic.
   </Callout>
  </section>
 </div>
);

export default JSFunctions;
